import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Hero from "@/components/Hero";
import EditorialMarquee from "@/components/EditorialMarquee";
import VenturesOverview from "@/components/VenturesOverview";
import CinematicStatement from "@/components/CinematicStatement";
import PartnersGrid from "@/components/PartnersGrid";
import { Reveal, Overline } from "@/components/Reveal";
import { SITE_URL, SITE_NAME, CONTACT_EMAIL } from "@/lib/site";
import { PILLARS } from "@/lib/data";
import { useLang } from "@/i18n";
import { ArrowRight, FileDown } from "lucide-react";

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/farmsons-logo.png`,
  email: CONTACT_EMAIL,
  foundingDate: "1985",
  description: "Registered farm produce trading company connecting growers across Himachal, Kashmir and India's mango belts to international B2B buyers.",
  address: { "@type": "PostalAddress", addressLocality: "Kolkata", addressRegion: "West Bengal", addressCountry: "IN" },
  areaServed: ["IN", "AE", "GB", "US", "CA"],
};

export default function Home() {
  const { t, data } = useLang();

  return (
    <>
      <SEO
        title="Farmsons — From Orchard to Export | Farm Produce Trading, India"
        description="Farmsons is a Kolkata-based farm produce trading company (est. 1985) connecting growers across Himachal, Kashmir and India's mango belts to international buyers — fruit trading, rootstock import and mango export."
        keywords="farm produce trading India, apple trading India, mango export India, apple import, Himachal apple supplier, Kolkata agtech"
        jsonLd={orgSchema}
      />
      <Hero />
      <EditorialMarquee />
      <VenturesOverview />
      <CinematicStatement />

      {/* Story teaser — internal links to the About page */}
      <section className="py-24 md:py-32 bg-[#ffffff] border-y border-[#1d3a1f]/5">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal><Overline>{t("home.storyOverline")}</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 mb-14 font-display font-bold tracking-tight leading-tight text-4xl md:text-5xl lg:text-6xl max-w-[18ch]">
              {t("home.storyHeading")}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-10">
            {PILLARS.map((c, i) => {
              const dp = data.pillars?.[i];
              return (
                <Reveal key={c.no} delay={i * 0.08}>
                  <div className="border-s border-[#1d3a1f]/10 ps-6">
                    <span className="font-display font-extrabold text-5xl text-[#d3a53a]">{c.no}</span>
                    <h3 className="mt-4 font-display font-bold text-xl md:text-2xl">{dp?.title || c.title}</h3>
                    <p className="mt-3 text-[#5f6b57] leading-relaxed">{dp?.body || c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <Link to="/about" className="mt-12 inline-flex items-center gap-2 text-sm font-bold text-[#d3a53a] hover:text-[#1d3a1f] transition-colors">
              {t("home.storyLink")} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Partners */}
      <section className="py-24 md:py-32 bg-[#fdfbf5]">
        <PartnersGrid />
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#ffffff] border-t border-[#1d3a1f]/5">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <Reveal>
            <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl max-w-[16ch]">{t("home.ctaHeading")}</h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/contact" className="group inline-flex items-center gap-2 bg-[#d3a53a] text-[#173a1e] font-bold px-8 py-4 rounded-full hover:bg-[#b98a26] transition-colors duration-200">
                {t("common.quote")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <a href="/farmsons-company-profile.pdf" target="_blank" rel="noopener noreferrer" data-testid="home-cta-profile"
                className="inline-flex items-center gap-2 border border-[#1d3a1f]/25 text-[#1d3a1f] font-bold px-8 py-4 rounded-full hover:border-[#d3a53a] hover:text-[#b98a26] transition-colors duration-200">
                <FileDown size={17} /> {t("common.downloadProfile")}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
